// src/containers/layout/receptionist-auth.tsx
//
// The Receptionist app shell, in the Oura look (ourteam-frontend#4785): the
// OuraSidebar the Oura merchant shell uses (open, or a 64px rail, with
// Collapse and the account at the foot) on the #F5F5F7 page ground. The mark
// is ourteam's own logo, not Oura's: this is the receptionist, not Oura.
// The nav keeps its three groups (Front desk, Your receptionist, Setup).

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronsUpDownIcon, LogOutIcon, PencilIcon, SettingsIcon, UserIcon } from "lucide-react";
import { RenameMerchantDialog } from "@receptionist/components/RenameMerchantDialog";
import { removeCookie } from "@util/cookie";
import { useUserProfileContext } from "@context/user_profile";
import { SnackbarProvider } from "@hook/use-snackbar";
import {
    receptionistNavFor,
    RECEPTIONIST_ROOT,
    RECEPTIONIST_SETTINGS_PATH,
} from "@receptionist/nav";
import { useReceptionistMerchantId, useReceptionistRole, type ReceptionistRole } from "@receptionist/role";
import { MERCHANT_NAME_EVENT, type MerchantNameDetail } from "@receptionist/merchantState";
import { getMerchant } from "@service/receptionist";
import { cn } from "@oura/lib/cn";
import { FOCUS_RING, OuraSidebar, PAGE_GROUND, useEscapeLayer, type OuraSidebarGroup } from "@oura/ui";

function handleLogout() {
    removeCookie("authToken");
    removeCookie("__receptionist_dev_role__");
    window.location.href = "/";
}

/** ourteam's mark, the logo this product has always carried. The same 32px open and closed. */
function BrandTile() {
    return (
        <img
            className="pointer-events-none h-8 w-auto"
            src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_dashboard.png`}
            alt="ourteam"
        />
    );
}

/** Initials from a name ("Jovi Chu" -> "JC") or, with no name, the email's first letter. */
function initialsOf(name: string): string {
    const words = name.includes("@") ? [name] : name.split(/\s+/).filter(Boolean);
    return words.map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

function Avatar({ name }: { name: string }) {
    const initials = initialsOf(name);
    return (
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700">
            {initials || <UserIcon aria-hidden size={16} />}
        </span>
    );
}

/**
 * Who is signed in, at the foot of the rail: the whole row open, the avatar
 * alone collapsed. Opens Settings and Logout beside the rail.
 */
function UserMenu({ collapsed }: { collapsed: boolean }) {
    const [, userInfo] = useUserProfileContext();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const button = useRef<HTMLButtonElement>(null);
    const [pos, setPos] = useState<CSSProperties | null>(null);

    useLayoutEffect(() => {
        if (!open) {
            setPos(null);
            return;
        }
        const measure = () => {
            const b = button.current?.getBoundingClientRect();
            if (!b) return;
            const GAP = 8;
            setPos({ left: b.right + GAP, bottom: Math.max(GAP, window.innerHeight - b.bottom) });
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [open]);

    const close = () => {
        setOpen(false);
        button.current?.focus();
    };
    useEscapeLayer(close, open);

    const fullName = [userInfo?.first_name, userInfo?.last_name].filter((p) => p && p.trim()).join(" ").trim();
    const email = userInfo?.email ?? "";
    const itemCls = "flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100";

    return (
        <div className={cn("relative", !collapsed && "w-full")}>
            <button
                ref={button}
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label="User menu"
                aria-expanded={open}
                className={cn(
                    collapsed
                        ? "flex items-center justify-center rounded-full"
                        : "flex w-full items-center gap-2.5 rounded-[9px] py-1.5 pl-2 pr-2.5 text-left transition-colors hover:bg-gray-50 active:bg-gray-100 active:duration-0",
                    open && !collapsed && "bg-gray-50",
                    FOCUS_RING,
                )}
            >
                <Avatar name={fullName || email} />
                {!collapsed && (
                    <>
                        <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-gray-900">{fullName || email || "Your account"}</span>
                            {fullName && email && <span className="block truncate text-xs text-gray-500">{email}</span>}
                        </span>
                        <ChevronsUpDownIcon aria-hidden size={14} className="shrink-0 text-gray-400" />
                    </>
                )}
            </button>

            {open && pos && createPortal(
                <>
                    <div aria-hidden className="fixed inset-0 z-30" onClick={close} />
                    <div style={pos} className="fixed z-40 w-56 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                        {collapsed && (fullName || email) && (
                            <div className="mb-1 border-b border-gray-100 px-4 pb-2 pt-1.5">
                                <p className="m-0 truncate text-sm font-medium text-gray-900">{fullName || email}</p>
                                {fullName && email && <p className="m-0 truncate text-xs text-gray-500">{email}</p>}
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false);
                                navigate(RECEPTIONIST_SETTINGS_PATH);
                            }}
                            className={itemCls}
                        >
                            <SettingsIcon className="mr-2 h-4 w-4" />
                            Settings
                        </button>
                        <button type="button" onClick={handleLogout} className={cn(itemCls, "border-t border-gray-100")}>
                            <LogOutIcon className="mr-2 h-4 w-4" />
                            Logout
                        </button>
                    </div>
                </>,
                document.body,
            )}
        </div>
    );
}

/**
 * The business name the merchant set on Business profile, not the workspace's
 * own name (which is the subdomain slug, "receptionist-one"). Read once, then
 * kept current by every read and save of the config (MERCHANT_NAME_EVENT).
 * Null while it loads, so the rail shows its placeholder rather than the slug;
 * the workspace name only if the read fails.
 */
function useBusinessName(merchantId: string | null, fallback: string): string | null {
    const [name, setName] = useState<string | null>(null);
    useEffect(() => {
        let live = true;
        // A read or save announced while the first read is out supersedes it
        // (Jack, #4795): a slow first read must not put the old name back.
        let requestIsCurrent = true;
        setName(null);
        if (!merchantId) {
            setName(fallback);
            return;
        }
        const onName = (e: Event) => {
            const d = (e as CustomEvent<MerchantNameDetail>).detail;
            if (d?.id !== merchantId) return;
            requestIsCurrent = false;
            setName(d.name.trim() ? d.name : fallback);
        };
        window.addEventListener(MERCHANT_NAME_EVENT, onName);
        getMerchant(merchantId)
            .then((r) => {
                if (!live || !requestIsCurrent) return;
                setName(r.ok && r.merchant.name.trim() ? r.merchant.name : fallback);
            })
            .catch(() => { if (live && requestIsCurrent) setName(fallback); });
        return () => {
            live = false;
            window.removeEventListener(MERCHANT_NAME_EVENT, onName);
        };
    }, [merchantId, fallback]);
    return name;
}

export default function ReceptionistAuthLayout({ children }: { children: ReactNode }) {
    const role = useReceptionistRole();
    const [, userInfo] = useUserProfileContext();
    const { pathname } = useLocation();

    const isActive = (to: string) => {
        const path = to.split("?")[0];
        if (path === `${RECEPTIONIST_ROOT}/merchants`) return pathname.startsWith(path);
        return pathname === path;
    };
    const groups: OuraSidebarGroup[] = receptionistNavFor(role).map((group) => ({
        key: group.label,
        label: group.label,
        items: group.items.map((item) => ({
            key: item.view,
            label: item.label,
            icon: item.icon,
            active: isActive(item.to),
            to: item.to,
        })),
    }));
    const base = import.meta.env.VITE_ASSET_OURTEAM_BASE_URL;
    const logo = userInfo?.company_logo ? `${base}/${userInfo.company_logo}` : null;
    const merchantId = useReceptionistMerchantId();
    const businessName = useBusinessName(merchantId, userInfo?.company_name || "Your business");
    const [renaming, setRenaming] = useState(false);

    return (
        <SnackbarProvider>
            {/* From 640px the page is the window's height and only the content
                column scrolls, so the rail never moves (as the Oura shell). */}
            <div className={cn("flex min-h-screen items-stretch sm:h-screen sm:overflow-hidden", PAGE_GROUND)}>
                <OuraSidebar
                    ariaLabel="Receptionist"
                    storageKey="receptionist.sidebar.collapsed"
                    brand={() => (
                        <Link to={receptionistHome(role)} className={cn("rounded", FOCUS_RING)}>
                            <BrandTile />
                        </Link>
                    )}
                    identity={{
                        name: businessName,
                        sub: "AI receptionist",
                        icon: logo,
                        // As Oura's: the name only, once a merchant's name has loaded.
                        action: merchantId && businessName !== null ? (
                            <button
                                type="button"
                                onClick={() => setRenaming(true)}
                                title="Edit the business name"
                                aria-label="Edit the business name"
                                className={cn("shrink-0 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700", FOCUS_RING)}
                            >
                                <PencilIcon className="h-3.5 w-3.5" />
                            </button>
                        ) : undefined,
                    }}
                    groups={groups}
                    foot={(collapsed) => (
                        <div className={cn("flex", collapsed && "justify-center")}>
                            <UserMenu collapsed={collapsed} />
                        </div>
                    )}
                />
                <main className="min-w-0 flex-1 overflow-y-auto px-4 pb-16 pt-8 sm:px-8 lg:px-12">{children}</main>
            </div>
            {renaming && merchantId && businessName !== null && (
                <RenameMerchantDialog merchantId={merchantId} name={businessName} onClose={() => setRenaming(false)} />
            )}
        </SnackbarProvider>
    );
}

function receptionistHome(role: ReceptionistRole): string {
    return role === "receptionist-staff"
        ? `${RECEPTIONIST_ROOT}/merchants`
        : `${RECEPTIONIST_ROOT}/home`;     // the Overview is a merchant's landing page (#4847)
}
